import { CURRENCY, LOCALE } from "@/core/constants";

const rupees = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  maximumFractionDigits: 0,
});
const rupeesExact = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const counts = new Intl.NumberFormat(LOCALE);

/** Amounts are integer paise from the API: ₹4,82,300 (paise shown only when there are some). */
export function formatPaise(paise: number): string {
  return (paise % 100 === 0 ? rupees : rupeesExact).format(paise / 100);
}

/** Like formatPaise, with a sign for money going out (−₹1,920) and in (+₹500). */
export function formatSignedPaise(paise: number): string {
  if (paise === 0) return formatPaise(0);
  return `${paise < 0 ? "−" : "+"}${formatPaise(Math.abs(paise))}`;
}

export function formatCount(n: number): string {
  return counts.format(n);
}

const plurals = new Intl.PluralRules(LOCALE);

/** A count with its noun: "1 item", "12 items", "1,250 meals" (plural defaults to noun + "s"). */
export function formatCountOf(n: number, noun: string, plural = `${noun}s`): string {
  return `${counts.format(n)} ${plurals.select(n) === "one" ? noun : plural}`;
}

/** "Thursday, 1 October" for a YYYY-MM-DD day (calendar day, no time zone shift). */
export function formatDayLong(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y!, m! - 1, d!)));
}

/** "1 Oct, 10:42 am" for a moment, in the given zone (default India). */
const percent = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });

/** Basis points as a percentage: 1250 → "12.5%", 5000 → "50%". */
export function formatBps(bps: number): string {
  return `${percent.format(bps / 100)}%`;
}

/** "1 Oct 2026" for a YYYY-MM-DD day (calendar day, no time zone shift). */
export function formatDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y!, m! - 1, d!)));
}

/** A calendar day `n` days later (or earlier, for negative n): "2026-10-31" + 1 → "2026-11-01". */
export function addDays(day: string, n: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
}

/** Today's date (YYYY-MM-DD) on the clock of `timeZone` (a brand's dated terms start "today" in its zone). */
export function todayIn(timeZone: string, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(now);
}

/** "1 Oct 2026": the calendar day of a moment, in India's time unless another zone is given. */
export function formatDate(iso: string, timeZone = "Asia/Kolkata"): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(new Date(iso));
}

export function formatMoment(iso: string, timeZone = "Asia/Kolkata"): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(new Date(iso));
}

const AGO_STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["minute", 60],
  ["hour", 24],
  ["day", 30],
  ["month", 12],
  ["year", Infinity],
];

/** How long ago a moment was: "just now", "5 minutes ago", "2 days ago", "3 months ago". */
export function formatAgo(iso: string, now = new Date()): string {
  let amount = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 60_000);
  if (amount < 1) return "just now";
  for (const [unit, perNext] of AGO_STEPS) {
    if (amount < perNext) {
      return new Intl.RelativeTimeFormat(LOCALE, { numeric: "always" }).format(-Math.floor(amount), unit);
    }
    amount /= perNext;
  }
  return "";
}

/** The zone's short name for headers ("IST"). */
export function zoneLabel(timeZone: string): string {
  const part = new Intl.DateTimeFormat("en-IN", { timeZone, timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName");
  return part?.value === "GMT+5:30" ? "IST" : (part?.value ?? timeZone);
}

/** "20:00" → "8:00 pm" (a wall-clock time as stored; no time zone conversion). */
export function formatClock(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const hour = h! % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${h! < 12 ? "am" : "pm"}`;
}
